/**
 * vore-cart.js — Add to cart AJAX da VORE, reescrito sem jQuery.
 *
 * Site de origem (INVENTORY 4.8): jQuery inline no layout.
 *   preventDefault -> label vira o texto de carregando + disabled -> POST /cart/add.js
 *   sucesso: restaura label, cartDrawer.open(), remove .is-empty,
 *            recarrega #CartDrawer e #cart-icon-bubble com .load(), initSlider() apos 2s
 *   erro: so um console.error, sem aviso nenhum na tela
 *
 * Aqui: Web Component no padrao do tema base.
 *   - estende Component (@theme/component), usa refs declarativos
 *   - fetch() + fetchConfig (@theme/utilities) + a rota cart_add_url do tema
 *     (o global window.KindTheme e do tema base — o nome nao pode mudar)
 *   - coleta as sections a re-renderizar via CartGroupedSections e as envia no POST
 *   - dispara CartAddEvent: o <cart-drawer-component> abre sozinho e faz o morph
 *     da section, e o <cart-count> atualiza a bubble. Sem .load(), sem initSlider().
 *   - erro do Shopify (422) exibido no proprio card, nao so no console
 *
 * Markup esperado (ver snippets/vore-ajax-add-to-cart.liquid):
 *   <vore-add-to-cart data-product-id="123">
 *     <form ref="form" ...>
 *       <input type="hidden" name="id" value="…">
 *       <input type="hidden" name="quantity" value="1">
 *       <button ref="button" data-adding-text="Adicionando..."><span ref="label">Adicionar</span></button>
 *     </form>
 *     <small ref="error" hidden></small>
 *   </vore-add-to-cart>
 */

import { Component } from "@theme/component";
import { CartAddEvent, CartErrorEvent, CartGroupedSections, ThemeEvents } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

const ERROR_TIMEOUT = 5000;

class VoreAddToCartComponent extends Component {
  requiredRefs = ["form", "button"];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimeout;

  /** @type {string} */
  #idleLabel = "";

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    this.refs.form?.addEventListener("submit", this.#handleSubmit, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  /**
   * Section ids que o tema quer re-renderizar junto com o /cart/add.js
   * (o cart drawer se inscreve via CartGroupedSections).
   * @returns {string[]}
   */
  #gatherGroupedSectionIds() {
    /** @type {string[]} */
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    event.preventDefault();

    const form = this.refs.form;
    const button = this.refs.button;

    if (!(form instanceof HTMLFormElement) || !(button instanceof HTMLButtonElement)) return;
    if (button.disabled) return;

    const formData = new FormData(form);
    const variantId = formData.get("id");

    if (!variantId) {
      console.error("vore-add-to-cart: variant id ausente no formulário.");
      return;
    }

    const groupedSectionIds = this.#gatherGroupedSectionIds();
    if (groupedSectionIds.length > 0) {
      formData.append("sections", groupedSectionIds.join(","));
    }

    this.#setLoading(true);
    this.#hideError();

    const routes = window.KindTheme?.routes ?? {};
    const addUrl = routes.cart_add_url || `${window.Shopify?.routes?.root ?? "/"}cart/add.js`;
    const config = fetchConfig("javascript", { body: formData });

    fetch(addUrl, {
      ...config,
      signal: this.#abortController.signal,
      headers: { ...config.headers, Accept: "text/html" },
    })
      .then((response) => response.json())
      .then((response) => {
        this.#setLoading(false);

        // Shopify devolve `status` quando a adicao falhou (ex.: 422 sem estoque)
        if (response.status) {
          this.#showError(response.description || response.message);

          this.dispatchEvent(new CartErrorEvent(this.id || "vore-add-to-cart", response.message));
          document.dispatchEvent(
            new CustomEvent(ThemeEvents.productAjaxError, {
              detail: { errorMessage: response.description || response.message },
            })
          );
          return;
        }

        this.dispatchEvent(
          new CartAddEvent(response, String(variantId), {
            source: "vore-add-to-cart",
            itemCount: Number(formData.get("quantity")) || 1,
            productId: this.dataset.productId,
            variantId: String(variantId),
            sections: response.sections,
          })
        );

        document.dispatchEvent(
          new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: response } })
        );
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        this.#setLoading(false);
        this.#showError(this.dataset.errorText || "Não foi possível adicionar. Tente de novo.");
        console.error("vore-add-to-cart:", error);
      });
  };

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const button = this.refs.button;
    const label = this.refs.label ?? button;

    if (!(button instanceof HTMLButtonElement)) return;

    if (isLoading) {
      this.#idleLabel = label instanceof HTMLElement ? label.textContent ?? "" : "";
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      if (label instanceof HTMLElement) {
        label.textContent = button.dataset.addingText || "Adicionando...";
      }
    } else {
      button.disabled = false;
      button.removeAttribute("aria-busy");
      if (label instanceof HTMLElement && this.#idleLabel) {
        label.textContent = this.#idleLabel;
      }
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const error = this.refs.error;
    if (!(error instanceof HTMLElement)) {
      console.error("vore-add-to-cart:", message);
      return;
    }

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);

    error.textContent = message;
    error.hidden = false;
    error.setAttribute("aria-live", "polite");

    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_TIMEOUT);
  }

  #hideError() {
    const error = this.refs.error;
    if (!(error instanceof HTMLElement)) return;
    error.hidden = true;
    error.removeAttribute("aria-live");
    error.textContent = "";
  }
}

if (!customElements.get("vore-add-to-cart")) {
  customElements.define("vore-add-to-cart", VoreAddToCartComponent);
}

export { VoreAddToCartComponent };
